import React, { useEffect, useId, useRef, useState } from "react";
import { X } from "lucide-react";
import { Button } from "../misc/Button";
import { Input } from "../misc/Input";
import { Label } from "../misc/Label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../misc/Select";
import { Textarea } from "../misc/Textarea";
import { US_STATES } from "../../utils/states";
import { NichePlace, NicheProfile } from "./types";

const LIMITS = { statement: 200, audience: 200, specialties: [8, 40], differentiators: [5, 60], areas: 12, town: 60 } as const;

// A name for a set of controls (a <Label> here would label nothing): role="group" named by
// an inline span with the Label's type styles, so it reads as before.
interface FieldGroupProps {
    label: string;
    className: string;
    children: React.ReactNode;
}

const FieldGroup: React.FC<FieldGroupProps> = ({ label, className, children }) => {
    const id = useId();
    return (
        <div role="group" aria-labelledby={id} className={className}>
            <span id={id} className="text-sm font-medium leading-none text-primary">{label}</span>
            {children}
        </div>
    );
};

interface TagFieldProps {
    label: string;
    addLabel: string;
    values: string[];
    max: number;
    maxLen: number;
    onChange: (v: string[]) => void;
}

const TagField: React.FC<TagFieldProps> = ({ label, addLabel, values, max, maxLen, onChange }) => {
    const [draft, setDraft] = useState("");
    const add = (): void => {
        const t = draft.trim();
        if (t && !values.some((v) => v.toLowerCase() === t.toLowerCase())) onChange([...values, t]);
        setDraft("");
    };
    return (
        <FieldGroup label={label} className="flex flex-col gap-1">
            <div className="flex flex-wrap gap-2">
                {values.map((v) => (
                    <span key={v} className="inline-flex items-center gap-1 rounded bg-gray-100 py-0.5 pl-2 pr-1 text-sm">
                        {v}
                        <Button type="button" variant="ghost" size="icon" className="h-6 w-6" aria-label={`Remove ${v}`} onClick={() => onChange(values.filter((x) => x !== v))}>
                            <X />
                        </Button>
                    </span>
                ))}
            </div>
            <Input
                aria-label={addLabel}
                placeholder={`${addLabel} and press Enter`}
                value={draft}
                maxLength={maxLen}
                disabled={values.length >= max}
                onChange={(e) => setDraft(e.target.value)}
                onBlur={add}
                onKeyDown={(e) => {
                    if (e.key === "Enter") {
                        e.preventDefault();
                        add();
                    }
                }}
            />
        </FieldGroup>
    );
};

interface PlaceFieldsProps {
    prefix: string;
    place: NichePlace;
    onChange: (p: NichePlace) => void;
}

const PlaceFields: React.FC<PlaceFieldsProps> = ({ prefix, place, onChange }) => (
    <div className="flex gap-2">
        <Input aria-label={`${prefix} town`} placeholder="Town" maxLength={LIMITS.town} value={place.town} onChange={(e) => onChange({ ...place, town: e.target.value })} />
        {/* Radix rejects an item whose value is "", so no state is the placeholder. */}
        <Select value={place.state} onValueChange={(state) => onChange({ ...place, state })}>
            <SelectTrigger aria-label={`${prefix} state`} className="w-24 shrink-0">
                <SelectValue placeholder="State" />
            </SelectTrigger>
            <SelectContent>
                {US_STATES.map((s) => (
                    <SelectItem key={s.value} value={s.value}>{s.value}</SelectItem>
                ))}
            </SelectContent>
        </Select>
    </div>
);

interface NicheProfileFormProps {
    initial: NicheProfile;
    submitLabel: string;
    onSubmit: (p: NicheProfile) => Promise<void> | void;
    errors?: string[];
    busy?: boolean;
    secondary?: React.ReactNode;
}

const NicheProfileForm: React.FC<NicheProfileFormProps> = ({ initial, submitLabel, onSubmit, errors, busy, secondary }) => {
    const [p, setP] = useState<NicheProfile>(initial);
    const set = <K extends keyof NicheProfile>(k: K, v: NicheProfile[K]): void => setP((cur) => ({ ...cur, [k]: v }));
    // The errors sit above a long form and Save sits at the bottom, so on a phone a failed
    // save would look like nothing happened: bring each new set of errors into view.
    const alertRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (!errors?.length || !alertRef.current) return;
        alertRef.current.scrollIntoView?.({ block: "center" });
        alertRef.current.focus();
    }, [errors]);

    return (
        <form
            className="space-y-4"
            // Enter in a one-line field would save the whole form (implicit submission) and, on
            // the interview's review step, end the review. Only Save saves; textareas keep their
            // newline, and a tag input's own Enter handler still adds the tag.
            onKeyDown={(e) => {
                if (e.key === "Enter" && e.target instanceof HTMLInputElement) e.preventDefault();
            }}
            onSubmit={(e) => {
                e.preventDefault();
                void onSubmit({
                    ...p,
                    nicheStatement: p.nicheStatement.trim(),
                    audience: p.audience.trim(),
                    // A new row starts on the home-base state, so a row with no town is one left blank.
                    serviceAreas: p.serviceAreas.filter((a) => a.town.trim()),
                });
            }}
        >
            {errors && errors.length > 0 && (
                <div ref={alertRef} tabIndex={-1} role="alert" className="rounded border border-red-500 p-3 text-sm text-red-500 focus:outline-none">
                    {errors.map((e) => <p key={e}>{e}</p>)}
                </div>
            )}
            <div className="flex flex-col gap-1">
                <Label htmlFor="niche-statement" className="text-primary">Niche statement</Label>
                <Textarea id="niche-statement" rows={4} maxLength={LIMITS.statement} value={p.nicheStatement} onChange={(e) => set("nicheStatement", e.target.value)} />
            </div>
            <div className="flex flex-col gap-1">
                <Label htmlFor="niche-audience" className="text-primary">Who I serve</Label>
                <Input id="niche-audience" maxLength={LIMITS.audience} value={p.audience} onChange={(e) => set("audience", e.target.value)} />
            </div>
            <TagField label="Specialties" addLabel="Add a specialty" values={p.specialties} max={LIMITS.specialties[0]} maxLen={LIMITS.specialties[1]} onChange={(v) => set("specialties", v)} />
            <FieldGroup label="Home base" className="flex flex-col gap-1">
                <PlaceFields prefix="Home base" place={p.homeBase} onChange={(v) => set("homeBase", v)} />
            </FieldGroup>
            <FieldGroup label="Areas I serve" className="flex flex-col gap-2">
                {p.serviceAreas.map((a, i) => (
                    <div key={i} className="flex gap-2 items-center">
                        <PlaceFields prefix={`Area ${i + 1}`} place={a} onChange={(v) => set("serviceAreas", p.serviceAreas.map((x, j) => (j === i ? v : x)))} />
                        <Button type="button" variant="ghost" size="icon" aria-label={`Remove area ${i + 1}`} onClick={() => set("serviceAreas", p.serviceAreas.filter((_, j) => j !== i))}>
                            <X />
                        </Button>
                    </div>
                ))}
                {p.serviceAreas.length < LIMITS.areas && (
                    <Button type="button" variant="outline" className="self-start" onClick={() => set("serviceAreas", [...p.serviceAreas, { town: "", state: p.homeBase.state }])}>
                        Add an area
                    </Button>
                )}
            </FieldGroup>
            <TagField label="What makes me different" addLabel="Add a differentiator" values={p.differentiators} max={LIMITS.differentiators[0]} maxLen={LIMITS.differentiators[1]} onChange={(v) => set("differentiators", v)} />
            <div className="flex gap-2">
                <Button type="submit" disabled={busy}>{submitLabel}</Button>
                {secondary}
            </div>
        </form>
    );
};

export default NicheProfileForm;
